/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, hasRefresh, request, setLogoutHandler, setSession } from './api';

export interface Me { id: string; email: string; fullName: string; role: string; permissions: string[]; modules: string[]; mustChangePassword?: boolean }
interface Ctx {
  me: Me | null; loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<'ok' | 'totp'>;
  logout: () => Promise<void>; can: (p: string) => boolean; hasModule: (m: string) => boolean; reload: () => Promise<void>;
}

const AuthCtx = createContext<Ctx>(null as any);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try { setMe(await api.get<Me>('/auth/me')); } catch { setMe(null); }
  }, []);

  useEffect(() => {
    setLogoutHandler(() => { setSession(null); setMe(null); });
    (hasRefresh() ? reload() : Promise.resolve()).finally(() => setLoading(false));
  }, [reload]);

  const login = async (email: string, password: string, totp?: string) => {
    const r = await request<any>('POST', '/auth/login', { email, password, totp });
    if (r.totpRequired) return 'totp' as const;
    setSession(r);
    await reload();
    return 'ok' as const;
  };

  const logout = async () => {
    let rt: string | null = null;
    try { rt = localStorage.getItem('kd.rt'); } catch { /* */ }
    if (rt) await request('POST', '/auth/logout', { refreshToken: rt }).catch(() => {});
    setSession(null);
    setMe(null);
  };

  const can = (p: string) => !!me?.permissions.includes(p);
  const hasModule = (m: string) => !!me?.modules.includes(m);
  return <AuthCtx.Provider value={{ me, loading, login, logout, can, hasModule, reload }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
